import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { X, HelpCircle } from 'lucide-react';
import { useGameStore } from '../../store/gameStore';

export const GameHelp = () => {
  const guideCharacter = useGameStore(state => state.guideCharacter);
  const [showHelp, setShowHelp] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if user has seen help before
    const hasSeenHelp = localStorage.getItem('murder-mystery-help-dismissed');
    if (!hasSeenHelp) {
      setShowHelp(true);
    } else {
      setDismissed(true);
    }
  }, []);

  const handleDismiss = () => {
    setShowHelp(false);
    setDismissed(true);
    localStorage.setItem('murder-mystery-help-dismissed', 'true');
  };

  const toggleHelp = () => {
    setShowHelp(!showHelp);
  };

  if (!showHelp && dismissed) {
    // Show small help button
    return (
      <button
        onClick={toggleHelp}
        className="fixed bottom-24 right-6 z-50 w-12 h-12 rounded-full bg-slate-800 hover:bg-slate-700 border-2 border-white/20 flex items-center justify-center shadow-lg transition-all duration-300"
      >
        <HelpCircle className="w-6 h-6 text-white" />
      </button>
    );
  }

  if (!showHelp) return null;

  return (
    <div role="dialog" aria-labelledby="game-help-heading" data-state="open" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <Card className="max-w-2xl w-full bg-slate-900 border-white/20 p-6 relative">
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="space-y-6">
          <div>
            <h2 id="game-help-heading" className="text-3xl font-bold text-white mb-2">How to Play</h2>
            <p className="text-gray-300">Here's how to solve the case:</p>
          </div>

          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="w-12 h-12 rounded-full bg-crimson flex items-center justify-center flex-shrink-0 text-white font-bold">
                1
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">Examine Locations</h3>
                <p className="text-gray-400 text-sm">
                  Use <span className="text-white font-semibold">"Examine Location"</span> to search the current area for clues. Clues will automatically appear in the right panel when discovered.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="w-12 h-12 rounded-full bg-crimson flex items-center justify-center flex-shrink-0 text-white font-bold">
                2
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">Interview & Travel</h3>
                <p className="text-gray-400 text-sm">
                  Interview suspects, read documents, and travel to new locations using the <span className="text-white font-semibold">action menu at the bottom</span>.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="w-12 h-12 rounded-full bg-crimson flex items-center justify-center flex-shrink-0 text-white font-bold">
                3
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">Consult {guideCharacter?.name || 'Your Contact'}</h3>
                <p className="text-gray-400 text-sm">
                  {guideCharacter?.name || 'Your contact'} can provide hints, but use them wisely - you have limited consultations.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0 text-white font-bold">
                4
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">Make Your Accusation</h3>
                <p className="text-gray-400 text-sm">
                  When ready, click <span className="text-red-400 font-semibold">ACCUSE</span> to name the killer. Choose carefully - you only get one chance!
                </p>
              </div>
            </div>
          </div>

          <div className="pt-4 border-t border-white/10">
            <Button
              autoFocus
              onClick={handleDismiss}
              className="w-full bg-crimson hover:bg-red-700 text-white font-semibold"
            >
              Start Investigation
            </Button>
            <p className="text-xs text-gray-500 text-center mt-2">
              You can access this help anytime by clicking the ? button
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};
